import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { agentsApi } from '../api/agentsApi';
import { agentsKeys } from '../queryKeys';
import type { AgentTask } from '../types';

// States where Sam is actively working ON A VISIBLE TASK, so the fast poll applies. Parked states
// (awaiting_approval / needs_review / blocked) and terminal states (done / cancelled / failed) don't
// change on their own account — but a BRAND NEW task can still appear with no human action in this
// UI at all: task_generation, for one, is created by the join-gate's server-side 30-minute timeout
// sweep (joinGate.ts), not by anything the user clicks here. Stopping polling entirely once every
// visible task looks settled would mean that new task never gets discovered without a manual reload
// (a real gap this comment used to miss) — so idle locations fall back to a slow background poll
// instead of stopping, trading a little request volume for actually finding server-triggered tasks.
const ACTIVE_STATES: readonly AgentTask['state'][] = ['ready', 'in_progress'];

const ACTIVE_POLL_MS = 3000;
const IDLE_POLL_MS = 30000;

export function useAgentTasks(enabled = true) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const query = useQuery({
    queryKey: agentsKeys.tasks(locationId),
    queryFn: () => agentsApi.listTasks(locationId),
    enabled: !!locationId && enabled,
    // Live updates without manual reload: poll fast while a visible task is active, slow otherwise —
    // never stop, so a task created by a server-side trigger still surfaces on its own.
    refetchInterval: (q) => {
      const tasks = q.state.data ?? [];
      return tasks.some((t) => ACTIVE_STATES.includes(t.state)) ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    },
    refetchIntervalInBackground: false,
  });

  return { ...query, tasks: query.data ?? [], locationId };
}
